import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, HelpCircle, X } from 'lucide-react';

export type FeedbackType = 'success' | 'error' | 'warning' | 'confirm';

interface FeedbackDialogProps {
    isOpen: boolean;
    onClose: () => void;
    type: FeedbackType;
    title: string;
    message: string;
    primaryActionLabel?: string;
    onPrimaryAction?: () => void;
    secondaryActionLabel?: string;
    onSecondaryAction?: () => void;
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
    isOpen,
    onClose,
    type,
    title,
    message,
    primaryActionLabel = 'OK',
    onPrimaryAction,
    secondaryActionLabel,
    onSecondaryAction,
}) => {
    const getIcon = () => {
        switch (type) {
            case 'success':
                return <CheckCircle2 className="w-12 h-12 text-green-500" />;
            case 'error':
                return <XCircle className="w-12 h-12 text-red-500" />;
            case 'warning':
                return <AlertTriangle className="w-12 h-12 text-yellow-500" />;
            case 'confirm':
                return <HelpCircle className="w-12 h-12 text-blue-500" />;
        }
    };

    const getAccentColor = () => {
        switch (type) {
            case 'success': return 'bg-green-500';
            case 'error': return 'bg-red-500';
            case 'warning': return 'bg-yellow-500';
            case 'confirm': return 'bg-blue-600';
        }
    };

    const handlePrimary = () => {
        if (onPrimaryAction) onPrimaryAction();
        onClose();
    };

    const handleSecondary = () => {
        if (onSecondaryAction) onSecondaryAction();
        onClose();
    };

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className={`h-2 w-full ${getAccentColor()}`} />
                                <div className="absolute right-4 top-4">
                                    <button
                                        type="button"
                                        className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 transition-colors"
                                        onClick={onClose}
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>

                                <div className="bg-white px-6 pb-8 pt-10 sm:p-10 sm:pt-12">
                                    <div className="flex flex-col items-center text-center">
                                        <div className="mb-6 rounded-full bg-gray-50 p-3 ring-8 ring-gray-50/50">
                                            {getIcon()}
                                        </div>
                                        <Dialog.Title as="h3" className="text-2xl font-bold leading-6 text-gray-900 mb-2">
                                            {title}
                                        </Dialog.Title>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500 whitespace-pre-wrap">
                                                {message}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row-reverse gap-3">
                                    <button
                                        type="button"
                                        className={`inline-flex w-full justify-center rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-90 transition-all sm:w-auto ${getAccentColor()}`}
                                        onClick={handlePrimary}
                                    >
                                        {primaryActionLabel}
                                    </button>
                                    {secondaryActionLabel && (
                                        <button
                                            type="button"
                                            className="inline-flex w-full justify-center rounded-xl bg-white px-6 py-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-all sm:w-auto"
                                            onClick={handleSecondary}
                                        >
                                            {secondaryActionLabel}
                                        </button>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default FeedbackDialog;
